import { IAsset } from '@models/assets/request';
import { ApiResponse } from '@models/common/api';
import { ICompleteLocationRequest, ICreateLocationRequest, IUpdateLocationRequest } from '@models/location/request';
import {
  IFindCategoryResponse,
  IFindCountryResponse,
  IFindOneLocationResponse,
  IFindLocationResponse,
  ICreateUpdateLocationResponse,
} from '@models/location/response';
import { IPublicHoliday } from '@models/schedule/public-holiday';

import { del, get, post, put } from './methods';

const BASE_ENDPOINT = 'locations';

const locationAPI = {
  find: async (organizationId: number) => {
    const params = { organizationId };
    const response = await get<ApiResponse<IFindLocationResponse>>(BASE_ENDPOINT, { params });
    return response.data;
  },

  findByBrandId: async (brandId: string, organizationId: number) => {
    // TEMPORARY MOCK: Return mock location data for testing
    // Remove this when backend is implemented
    // Enable mock by setting localStorage.setItem('mock_empty_locations', 'true') for empty
    // or localStorage.setItem('mock_locations', 'true') for sample data
    const shouldMockEmpty = localStorage.getItem('mock_empty_locations') === 'true' || brandId === 'mock-empty';
    const shouldMockData = localStorage.getItem('mock_locations') === 'true';

    if (shouldMockEmpty) {
      return Promise.resolve({
        success: true,
        data: [],
        message: 'Mock empty response for testing',
      });
    }

    if (shouldMockData) {
      return Promise.resolve({
        success: true,
        data: [],
        message: 'Mock location data for testing',
      });
    }

    const params = { brandId, organizationId };
    const response = await get<ApiResponse<IFindLocationResponse>>(BASE_ENDPOINT, { params });
    return response.data;
  },

  findOne: async (id: number) => {
    const response = await get<ApiResponse<IFindOneLocationResponse>>(`${BASE_ENDPOINT}/${id}`);
    return response.data;
  },

  create: async (body: ICreateLocationRequest) => {
    // TEMPORARY MOCK: Return success for testing until backend is implemented
    if (localStorage.getItem('mock_locations') === 'true' || localStorage.getItem('mock_empty_locations') === 'true') {
      return Promise.resolve({
        success: true,
        data: {
          id: Math.floor(Math.random() * 1000) + 100,
          name: body.name,
          categoryName: '',
          street: body.street,
          city: body.city,
          postalCode: body.postalCode,
          countryIso: body.countryIso,
          stateIso: body.stateIso ?? '',
          latitude: 0,
          longitude: 0,
          phone: body.phone,
          description: body.description,
          businessHours: [],
          photos: [],
          createdAt: new Date().toISOString(),
        },
        message: 'Location created successfully (mock)',
      });
    }

    const response = await post<ApiResponse<ICreateUpdateLocationResponse>>(BASE_ENDPOINT, body);
    return response.data;
  },

  update: async (id: number, body: IUpdateLocationRequest) => {
    const response = await put<ApiResponse<ICreateUpdateLocationResponse>>(`${BASE_ENDPOINT}/${id}`, body);
    return response.data;
  },

  delete: async (id: number) => {
    const response = await del<ApiResponse<ICreateUpdateLocationResponse>>(`${BASE_ENDPOINT}/${id}`);
    return response.data;
  },

  categories: async () => {
    const response = await get<ApiResponse<IFindCategoryResponse[]>>(`${BASE_ENDPOINT}/categories`, undefined, 2);
    return response.data;
  },

  countries: async () => {
    const response = await get<ApiResponse<IFindCountryResponse[]>>(`${BASE_ENDPOINT}/countries`, undefined, 2);
    return response.data;
  },

  uploadPhoto: async (locationId: number, body: { photos: IAsset[] }) => {
    const response = await post<ApiResponse<unknown>>(`${BASE_ENDPOINT}/${locationId}/photos`, body);
    return response;
  },
  deletePhotos: async (locationId: number, body: string[]) => {
    const response = await del<ApiResponse<unknown>>(`${BASE_ENDPOINT}/${locationId}/photos`, {
      data: { photoIds: body },
    });
    return response;
  },

  listingsPlan: async ({ organizationId, renewalStartDate }: { organizationId: number; renewalStartDate: string }) => {
    const response = await put<ApiResponse<string>>(`${BASE_ENDPOINT}/listings-plan`, {
      organizationId,
      renewalStartDate,
    });
    return response.data;
  },

  publicHolidays: async (params: { countryCode: string; daysFromNow: number }) => {
    const response = await get<ApiResponse<IPublicHoliday[]>>(`${BASE_ENDPOINT}/public-holidays`, { params }, 1);
    return response.data;
  },

  // Spartacus endpoints
  spartacus: {
    findByBrandId: async (organizationId: number, brandId: number) => {
      const params = { organizationId, brandId };
      const response = await get<ApiResponse<IFindLocationResponse>>('spartacus/locations', { params });
      return response.data;
    },

    create: async (body: ICreateLocationRequest & { brandId: number }) => {
      const response = await post<ApiResponse<ICreateUpdateLocationResponse>>('spartacus/locations', body);
      return response.data;
    },

    // POST /v1/spartacus/locations/complete — completes an existing phantom Location
    // (synupLocationId === null). Use this instead of create when the Location row
    // already exists; the plain create stays for when there's no Location at all.
    complete: async (body: ICompleteLocationRequest) => {
      const response = await post<ApiResponse<ICreateUpdateLocationResponse>>('spartacus/locations/complete', body);
      return response.data;
    },
  },
};

export default locationAPI;
